<template>
  <div class="table-container" v-if="canVerb(resource, 'index')">
    <actions-toolbar>
      <template #filters>
        <div class="status-filter">
          <div class="pill-wrapper my-3">
            <span
              class="pill"
              v-for="(value, name, index) in activeFilters"
              :key="index"
              data-color="white"
            >
              <span v-b-toggle.sidebar-1 :data-filter="name"
                ><strong>{{ sidebarFilterName(name, context) }}</strong
                >:&nbsp;{{ sidebarFilterValue(name, value) }}</span
              >
              <span tabindex="0">
                <span
                  @click="resetSingleSidebarFilter(name)"
                  title="Rimuovi filtro"
                  >×</span
                >
              </span>
            </span>
            <template v-if="countActiveFilters">
              <span
                class="pill"
                data-color="alert"
                @click="resetAllFilters()"
                title="Rimuovi i filtri e apri il pannello per una nuova ricerca"
              >
                <span
                  ><strong>Reset ({{ countActiveFilters }})</strong></span
                >
                <span><span>×</span></span>
              </span>
            </template>
          </div>
        </div>
      </template>
      <b-button-group
        v-b-toggle.sidebar-1
        :class="`filter-button-group btn--smaller btn--transparent align-items-center gap-3${
          countActiveFilters ? ' active' : ''
        }`"
        :title="getDefaultFilterMessage()"
      >
        <base-icon name="filters" width="23" height="23" />
        <span style="font-size: 14px; font-weight: 500">{{
          computedFilterTitle
        }}</span>
      </b-button-group>
      <b-button
        type="button"
        class="btn--smaller btn--transparent"
        variant="secondary"
        size="sm"
        @click="onRefreshContent(filterName)"
        title="Refresh"
        ref="refreshReportTable"
      >
        <base-icon name="reload" width="20" height="20" />
        Ricarica
      </b-button>
    </actions-toolbar>

    <div class="table-container">
      <b-sidebar
        id="sidebar-1"
        :title="computedFilterTitle"
        :backdrop="true"
        width="50dvw"
        shadow
        right
        :visible="open_filter"
        :data-open="open_filter"
        @change="onSidebarFilterChange"
      >
        <b-form @submit.prevent="onSearch(filterName)">
          <div class="filter">
            <div class="filter-item" data-type="report">
              <base-select
                name="code"
                label="Report"
                v-model="filter.byReport.code"
                :options="reportOptions"
              />
            </div>
            <div class="filter-item" v-if="hasUserAdminRole">
              <base-input
                name="username"
                label="Nome Utente"
                type="text"
                v-model="filter.byUser.username"
                placeholder="Inserisci un nome utente"
              />
            </div>
            <div class="filter-item" data-cols="2">
              <base-datepicker
                name="creazione_da"
                label="Data creazione da"
                v-model="filter.byCalendar.from"
              />
              <base-datepicker
                name="creazione_a"
                label="Data creazione a"
                v-model="filter.byCalendar.to"
              />
            </div>
          </div>
          <div class="actions-filter-sidebar-wrapper">
            <b-button
              type="submit"
              variant="lisaweb"
              size="sm"
              v-b-toggle:sidebar-1
              >Cerca
            </b-button>
            <b-button
              class="btn--transparent btn-reset"
              type="button"
              size="sm"
              @click="onClearFilter(filterName)"
              >Reset
            </b-button>
          </div>
        </b-form>
      </b-sidebar>
      <generated-reports
        :fields="fields"
        :repository="repository"
        :resource="resource"
        @manualFetch="manualFetch"
        :performedSearch="computePerformedSearch"
        :filterCount="countActiveFilters"
        :onlyActions="['destroy']"
        :filterOn="{
          byRelations: ['byReport', 'byDocument'],
        }"
        :filterName="filterName"
        :ref="tableRef"
        descending
        sortField="created_at"
        @destroy="onDestroy"
        @report="downloadReport"
      ></generated-reports>
    </div>
  </div>
  <div v-else class="mt-3">
    <b-icon icon="info-circle" scale="1.00"></b-icon>
    Non hai i permessi
  </div>
</template>

<script>
import CustomFiltersMixin from "@/mixins/CustomFiltersMixin";
import DocumentsMixin from "@/mixins/DocumentsMixin";
import StorageGetterMixin from "@/mixins/StorageGetterMixin";
import GeneratedReports from "@/components/tables/GeneratedReports.vue";
import { mapGetters, mapActions } from "vuex";
import BaseInput from "@/components/form/BaseInput";
import BaseSelect from "@/components/form/BaseSelect";
import BaseDatepicker from "@/components/form/BaseDatepicker";
import { toLocaleDate } from "@/utils/dates";
import { isAdmin, canVerb } from "@/utils/permissions";
import BaseIcon from "@/components/BaseIcon";
import SidebarFilterMixin from "../../../mixins/SidebarFilterMixin";
import { RepositoryFactory } from "@/repositories/RepositoryFactory";

export default {
  mixins: [
    CustomFiltersMixin,
    DocumentsMixin,
    StorageGetterMixin,
    SidebarFilterMixin,
  ],
  data() {
    return {
      repository: "generated_report",
      resource: "generated_reports",
      context: "generated_reports",
      // reportsAvailable: ["AGGRECLI"],
      tableRef: "GeneratedReportFilter",
      filterName: "filterGeneratedReport",
      activeFilters: {},
      performedSearch: false,
      filter: this.initFilter(),
      fields: [
        {
          key: "report.title",
          label: this.getDictionary("formatted_title", "generated_report"),
          sortable: true,
          sortKey: "byReport.title",
        },
        {
          key: "report.code",
          label: this.getDictionary("code", "generated_report"),
          sortable: true,
          sortKey: "byReport.code",
        },
        {
          key: "format",
          label: this.getDictionary("format", "generated_report"),
        },
        {
          key: "status_report.text",
          label: this.getDictionary("status_report", "generated_report"),
        },
        {
          key: "is_ready.text",
          label: this.getDictionary("is_ready", "generated_report"),
        },
        {
          key: "user",
          label: this.getDictionary("username", "user"),
          // sortable: true,
          // sortKey: "byUser.username",
        },
        {
          key: "created_date",
          label: this.getDictionary("created_at"),
          sortable: true,
          formatter: (value) => toLocaleDate(value, "DD/MM/YYYY HH:mm:ss"),
          sortKey: "created_at",
        },
        {
          key: "download",
          label: this.getDictionary("download", "generated_report"),
          tdClass: "text-center",
          thStyle: {
            width: "125px",
          },
        },
      ],
      isUserAdmin: false,
      reports: [],
      reportOptions: [],
      // reportOptions: [
      //   { text: "ESPORTAZIONE POLIZZE ASSICURATIVE", value: "EXPOINPO" },
      //   { text: "ESPORTAZIONE SINISTRI", value: "EXPOCLAI" },
      //   { text: "ESPORTAZIONE ANAGRAFICHE", value: "EXPOREGI" },
      // ],
    };
  },
  components: {
    BaseIcon,
    GeneratedReports,
    BaseInput,
    BaseDatepicker,
    BaseSelect,
  },
  created() {
    this.isUserAdmin = isAdmin();
    /* */
    const path = "auth/";
    const resources = [];
    if (this.canVerb("reports", "index") && !this.hasLoadedResource("reports"))
      resources.push(
        this.$store
          .dispatch(`${path}reports`)
          .then(() => {})
          .catch((error) => {
            let errMsg = this.$getErrorMessage(error);
            this.$showSnackbar({ preset: "error", text: errMsg });
          }),
      );
    if (resources.length) {
      this.getResources(resources)
        .then(() => {
          this.getReports();
        })
        .catch((error) => {
          console.log("One or more promises crashed... :|");
          this.onFailure(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    } else {
      this.getReports();
      this.isLoading = false;
    }
    /* */
  },
  methods: {
    isAdmin,
    canVerb,
    downloadReport(document) {
      this.downloadDocument(document);
    },
    onDestroy(id) {
      const Repo = RepositoryFactory.get(this.repository);
      console.log("Repo", Repo);
      Repo.destroy(id)
        .then(() => {
          this.$refs[this.tableRef].fetch();
          this.$showSnackbar({
            preset: "success",
            text: "Report eliminato con successo",
          });
        })
        .catch((error) => {
          let errMsg = this.$getErrorMessage(error);
          this.$showSnackbar({
            preset: "error",
            text: `${errMsg}`,
          });
        });
    },
    initFilter() {
      let init = {
        byAttribute: {
          format: null,
        },
        byReport: {
          title: null,
          code: null, // TODO: to restrict viewable reports, set 'em here! ["AGGRECLI"],
        },
        byCalendar: {
          from: null,
          to: null,
        },
        byUser: {
          id: null,
          username: null,
        },
      };
      return init;
    },
    getReports() {
      this.reports = this.$store.state.auth.reports;
      // this.reports = this.$store.state.auth.reports.filter((e) =>
      //   this.reportsAvailable.includes(e.code)
      // );
      // this.reportOptions = [
      //   ...this.reportOptions,
      //   ...this.reports.map((e) => {
      //     return {
      //       text: e.title,
      //       value: e.code,
      //     };
      //   }),
      // ];
      // this.reportOptions.sort((a, b) => (a.text > b.text) - (a.text < b.text));
      if (this.reports.length <= 0) return;
      this.reportOptions = this.reports.map((e) => {
        return {
          text: `${e.title}
          ↪ ${e.code}`,
          value: e.code,
        };
      });
      this.reportOptions.sort((a, b) => (a.text > b.text) - (a.text < b.text));
      // this.reportOptions.sort((a, b) => {
      //   const textA = a.text.replace(/\[.*?\]\s*-\s*/, "");
      //   const textB = b.text.replace(/\[.*?\]\s*-\s*/, "");
      //   return textA > textB ? 1 : textA < textB ? -1 : 0;
      // });
    },
    onRefreshContent(name) {
      let criteria = this.filter;
      this.saveFilterByName({ name, criteria });
      this.$refs[this.tableRef].fetch();
    },
    manualFetch() {
      this.resetAllFilters();
    },
    retriveActiveFilters(obj = this.filter, parentKey = "") {
      for (const key in obj) {
        const value = obj[key];
        // Build the property path (for nested properties)
        const fullPath = parentKey ? `${parentKey}.${key}` : key;

        if (key === "byCustom" && typeof value === "object") {
          // Special handling for `byCustom`: Return everything in the inner object only if `value` is not null
          for (const innerKey in value) {
            const innerValue = value[innerKey];
            if (
              innerValue &&
              typeof innerValue === "object" &&
              "value" in innerValue
            ) {
              /* By default "value" is euqal to `null`, after it's been filled and being consequently emptied it's equal to an empty string */
              if (!(innerValue.value === null || innerValue.value === "")) {
                this.activeFilters[`${fullPath}.${innerKey}.value`] =
                  innerValue.value;
              }
            }
          }
        } else if (value && Array.isArray(value)) {
          if (value.length === 1) {
            this.activeFilters[fullPath] = value[0];
          } else if (value.length > 1) {
            // If it's an array don't print all the values recorsively
            this.activeFilters[fullPath] = `${value.length} selezioni`;
          }
        } else if (value && typeof value === "object") {
          // Recursively process nested objects for non-`byCustom` keys
          Object.assign(
            this.activeFilters,
            this.retriveActiveFilters(value, fullPath),
          );
        } else if (value !== null && String(value).length) {
          // Append non-null values
          this.activeFilters[fullPath] = value;
        }
      }
      return this.activeFilters;
    },
    onSearch(name) {
      this.performedSearch = true;
      this.activeFilters = {};
      this.activeFilters = this.retriveActiveFilters();
      // const role = this.getRoles()[0];
      // if (!role || role === "operatore")
      //   this.filter.byUser.id = this.$store.state.auth?.user?.id;
      // const user_header_nav = document.querySelector(".user-wrapper");
      // const user_name = user_header_nav.textContent.trim();
      // if (user_header_nav && user_header_nav.dataset.id)
      //   this.filter.byUser.id = +user_header_nav.dataset.id;
      // if (
      //   user_header_nav &&
      //   user_name &&
      //   (user_name.startsWith("admin.") || user_name === "amministratore")
      // )
      //   this.filter.byUser.id = null;
      let criteria = this.filter;
      this.saveFilterByName({ name, criteria });
      this.removePaginationByName(name);
      this.$refs[this.tableRef].currentPage = 1;
      this.$refs[this.tableRef].fetch();
    },
    onClearFilter(name) {
      this.resetFilter();
      this.resetPagination();
      this.removeFilterByName(name);
      this.removePaginationByName(name);
      this.removeSortByName(name);
      this.$refs[this.tableRef].sortBy = "created_at";
      this.$refs[this.tableRef].sortDesc = true;
    },
    ...mapActions("filters", {
      saveFilterByName: "saveByName",
      removeFilterByName: "removeByName",
    }),
    ...mapActions("pagination", { removePaginationByName: "removeByName" }),
    ...mapActions("sorts", {
      removeSortByName: "removeByName",
    }),
    ...mapGetters("auth", {
      getRoles: "roles",
    }),
    onSaveFilter(name, criteria) {
      this.saveFilterByName({ name, criteria });
    },
  },
  computed: {
    hasUserAdminRole() {
      return this.isUserAdmin;
    },
    countActiveFilters() {
      return Object.keys(this.activeFilters).length;
    },
    computedFilterTitle() {
      return `Filtri${
        this.countActiveFilters ? ` (${this.countActiveFilters})` : ""
      }`;
    },
    computePerformedSearch() {
      return this.performedSearch;
    },
  },
  mounted() {
    this.$root.$on("reloadReportsTable", this.onRefreshContent);
    // trigger an initial table load on page load (like /bank-accounts)
    this.$refs[this.tableRef].fetch();
  },
  beforeDestroy() {
    this.$root.$off("reloadReportsTable", this.onRefreshContent);
  },
};
</script>
<style lang="scss" scoped>
:deep(div[data-type="report"] .multiselect) {
  height: auto;
}

:deep(div[data-type="report"] .multiselect .multiselect__option span) {
  white-space: pre-line;
}

:deep(div[data-type="report"] .multiselect .multiselect__single) {
  white-space: pre-line;
}
</style>
